import { useState } from 'react'

import { BuyBox } from 'src/components/BuyBox/BuyBox'
import { VariantOptions } from 'src/components/BuyBox/VariantOptions'
import { CoffeeDetailsTable } from 'src/components/CoffeeDetailsTable/CoffeeDetailsTable'
import { Headline } from 'src/components/Headline/Headline'
import { Link } from 'src/components/Link/Link'
import { ProductCardImage } from 'src/components/ProductCard/ProductCardImage'
import { Text } from 'src/components/Text/Text'
import { PurchaseType } from 'src/constants'
import { useLocale } from 'src/contexts/LocaleContext'
import { removeBioOrganic } from 'src/functions/product/removeBioOrganic'
import { getMetafield, getMetafields } from 'src/functions/util/getMetafield'
import { pathHelper } from 'src/functions/util/pathHelper'

import styles from './SimilarProductModalContent.module.scss'

import type { Product } from 'src/data/graphql-generated'

interface SimilarProductModalContentProps {
  product: Product
}

export const SimilarProductModalContent = ({ product }: SimilarProductModalContentProps) => {
  const { language, locale } = useLocale()

  const {
    title,
    metafields,
    variants: { nodes: productVariants },
    images: { nodes: productImages },
    vendor,
    productType,
    handle,
  } = product

  const [firstImage] = productImages
  const [mainVariant] = productVariants

  const [variantSku, setVariantSku] = useState(mainVariant?.sku)

  const coffeeMetafields = getMetafields(metafields, 'coffee', locale)
  const coffeeTaste = getMetafield(metafields, 'coffee', 'taste', locale)
    ?.map((taste: Record<string, string>) => taste?.title)
    .join(', ')
  const coffeeCharacterAdjective = getMetafield(metafields, 'coffee', 'character_adjective', locale)?.title
  const coffeeDetailTaste = `${coffeeCharacterAdjective ? `${coffeeCharacterAdjective} | ` : ''}${coffeeTaste}`
  const coffeeDetailsToDisplay = coffeeMetafields.filter((metafield) =>
    ['strength', 'acidity', 'intensity', 'style'].includes(metafield.key),
  )

  const productTitleWithoutBio = removeBioOrganic(title)
  const secondaryText =
    (coffeeTaste && coffeeDetailTaste) ||
    (productVariants.length > 1 && mainVariant?.title) ||
    vendor ||
    'Coffee Circle'
  const variant = productVariants.find(({ sku }) => sku === variantSku) || mainVariant

  const productLink = pathHelper(productType, language, handle, mainVariant.sku, false, false)

  return (
    <div className={styles.wrapper}>
      <div className={styles.productInfoColumn}>
        <a href={productLink}>
          <ProductCardImage name={productTitleWithoutBio} image={firstImage} withPadding={false} />
        </a>

        <div className={styles.infoContainer}>
          <Link underline={false} href={productLink} stealth>
            <Headline variant="h3">{productTitleWithoutBio}</Headline>
          </Link>
          <Text variant="p">{secondaryText}</Text>

          {Boolean(coffeeDetailsToDisplay.length) && (
            <div className={styles.coffeeDetailsWrapper}>
              <CoffeeDetailsTable details={coffeeDetailsToDisplay} withoutModals />
            </div>
          )}
        </div>
      </div>

      <div className={styles.column}>
        <VariantOptions product={product} variant={variant} setVariantSku={setVariantSku} />
        <BuyBox product={product} variant={variant} isRenderedWithinModal purchaseType={PurchaseType.oneTime} />
      </div>
    </div>
  )
}
